/*
 * Which world a session opens in, as one constant.
 *
 * Phase 4 decision 7 — **session shape**: "the default URL is the curated seed
 * baked in". A playtester is sent a link with nothing after the slash, and what
 * they play has to be the world the scout picked rather than whatever the client
 * happens to default to. So the whole of "which world" is {@link SESSION_WORLD}
 * below: a seed, a bootstrap and a home town, in one place, changed in one edit.
 *
 * # How one constant reaches three readers
 *
 * The seed and the bootstrap are read by `world/wasm.ts` out of the query string
 * and the home town by `session/params.ts` out of the same place, so the way to
 * make one constant govern all three is to **fill in the parameters the URL did
 * not name** before anything reads them ({@link sessionWorldSearch}), and then
 * to put the filled-in query back in the address bar ({@link applySessionWorld}).
 *
 * That last step is deliberate rather than incidental, and it is worth saying
 * why out loud: **a playtest is only worth anything if the session can be
 * replayed**, and the replay file a player hands back carries the seed but not
 * the bootstrap (`sim-cli record`'s format is `(seed, commands, hashes)`). A URL
 * that names the world in full is the cheapest possible session capture — the
 * player copies what is in front of them, and the same link builds the same
 * world tomorrow. It is also what makes "I saw a war" answerable: the owner
 * opens the same link.
 *
 * Nothing here is a simulation input in the sense that matters: the client is
 * choosing *which world to build*, exactly as `?seed=` always has, and the
 * player is still not a sim entity (Phase 3 decision 10).
 *
 * # The seam this does not take
 *
 * The tidy version of this is `createWasmWorld({ seed, bootstrapYears })` —
 * parameters instead of a query string. That is a change to `world/wasm.ts`,
 * which wave 2A finalized and this chunk may not touch, so the URL is the seam
 * that was available. If a later chunk opens that file, the constant below is
 * the thing to pass in, and this module shrinks to {@link SESSION_WORLD}.
 */

/**
 * The world a session opens in when the URL says nothing: **the curated one**.
 *
 * Seed `0x1b`, 195 years of history, and the player put down in settlement 10 —
 * Helgasktarn. Picked by Phase 4's scout v2, re-affirmed by Phase 5's v3,
 * re-picked by Phase 6's v4, re-picked by Phase 7's wave 1G, and **re-picked
 * again by Phase 7's wave 2A**
 * (`cargo run -p sim-cli --release -- scout --seeds 0x1..0x40 --top 200`): the
 * caravan's traveller is drawn from a town's adults now, never from its ruler
 * or its champion, and at most once a season across every road — so the world
 * wave 1G handed out lost its anchor, which was a seat-holder walking a road he
 * can no longer be spared for. This is a new world rather than the old one
 * re-blessed.
 *
 * It is **rank three of 18,016 candidates**, and it is rank three rather than
 * rank one because it is the highest-ranked world that satisfies every sim-side
 * assert: rank one (`0x19` at 193) has an anchor from the war's victim whose
 * town is never sacked while the player is watching, and rank two is this same
 * world through a neighbour's eyes, which would want a `?home=` the default
 * spawn already gives for free.
 *
 * It is pinned sim-side by six tests, and a change to this constant the
 * simulation has not agreed to is a failing one of them rather than a session
 * nobody can reproduce:
 *
 * - `scout_the_curated_world_carries_the_criterion_at_distance`
 * - `scout_the_curated_worlds_session_delivers_the_arc`
 * - `scout_the_curated_worlds_beats_trace_to_records`
 * - `scout_the_curated_world_closes_the_reputation_loop`
 * - `scout_the_curated_world_has_a_book_within_reach`
 * - `scout_the_curated_world_declares_a_war_over_a_refusal`
 *
 * What the scout picked it for, which is also what a session should meet: the
 * word channel is live at Helgasktarn on the opening day (record #2259, two
 * tellings out), the anchor arrives that same day with a caravan — Inggarding of
 * Eiraldford, figure #225, the traveller the simulation picked for that pulse —
 * and on day 28 the war reaches their town: battle #2265 sacks Eiraldford, and
 * the board's shock #2278 cites the war's earlier battle #2254 as an import
 * (Phase 4 decision 3's contagion, arriving as a number). The whole arc closes
 * inside the first entity turn.
 *
 * And what Phase 5 added to the same list, because its verbs need somewhere to
 * happen: **a book on the shelf of the home town itself** — chronicle #5, left
 * in Helgasktarn by Rakmor in year 182, so the historian verb opens on a book
 * the player is standing next to — and **a word to carry**: Vorgardbeck, one
 * road out, has not heard record #1870, and telling them puts the player's deed
 * in ten towns' rumor stores by day 28, Helgasktarn's own among them. Neither is
 * arranged; both are read off the world the seed produced.
 *
 * And what Phase 6 added: **nineteen of this world's wars were declared over a
 * refusal** — nine over a tribute demand thrown back and ten over a call for
 * help left unanswered. That is the phase's own exit criterion — *"the peace
 * refused, the sack that followed"* — standing in the world the link opens, at
 * the record level, before any of it is prose.
 *
 * Every record number above is printed by the sim-side tests under
 * `--nocapture`, which is where to re-derive them from when they move again.
 *
 * Three fields, and each is here rather than in the module that reads it because
 * "which world" is one decision:
 *
 * - `seed` — as a string, so it can be written the way a scout report prints it
 *   and handed to the URL untouched. `world/wasm.ts` is what turns it into the
 *   `BigInt` the simulation takes.
 * - `bootstrapYears` — 195, against `world/wasm.ts`'s own default of 200. The
 *   two numbers answer different questions: that one is "how much history does a
 *   client that was told nothing build" and this one is "how much history does
 *   the *curated session* stand on". The gap is the point of this file.
 * - `home` — **belt-and-braces on this world, where it used to be load-bearing.**
 *   Helgasktarn (#10) *is* the spawn the seed itself produces, so the link would
 *   open the same session without it. It stays because the default spawn is a
 *   *rule* — the settlement nearest the origin — and a rule can move under a
 *   worldgen change while a number cannot. It is the settlement id rather than
 *   the name because that is what the sim-side assert pins and what a scout
 *   report prints; `session/params.ts` resolves either.
 */
export const SESSION_WORLD: SessionWorld = {
  seed: "0x1b",
  bootstrapYears: 195,
  home: "10",
};

/** The three things that decide which world a session is. */
export interface SessionWorld {
  /** The seed, spelled as a URL would carry it: decimal or `0x` hex. */
  readonly seed: string;

  /** Years of history built before the first frame. */
  readonly bootstrapYears: number;

  /** The settlement to wake up in, by name or id, or `null` for the seed's own spawn. */
  readonly home: string | null;
}

/** The parameters this module fills in. Spelled once; every reader is elsewhere. */
const SEED_PARAM = "seed";
const BOOTSTRAP_PARAM = "bootstrap";
const HOME_PARAM = "home";

/**
 * Returns the query string with the session's world filled into whatever it did
 * not already name.
 *
 * **Absent means "use the curated world"; present means "the URL meant it".**
 * That ordering is the whole rule, and it is what keeps every existing tool
 * working unchanged: `?bootstrap=0` is still the Phase 1 world with no history,
 * `?seed=1234` still opens somebody else's, and `node scripts/live-smoke.mjs
 * --bootstrap 200` still measures the world it asked for. Only a parameter
 * nobody named is answered from here.
 *
 * A pure function of a string, like everything else in this directory, so the
 * self-checks can ask the awkward questions without a browser
 * (`session/params.ts` states the same rule).
 */
export function sessionWorldSearch(search: string, world: SessionWorld = SESSION_WORLD): string {
  const params = new URLSearchParams(search);

  if (!named(params, SEED_PARAM)) {
    params.set(SEED_PARAM, world.seed);
  }

  if (!named(params, BOOTSTRAP_PARAM)) {
    params.set(BOOTSTRAP_PARAM, String(world.bootstrapYears));
  }

  if (world.home !== null && !named(params, HOME_PARAM)) {
    params.set(HOME_PARAM, world.home);
  }

  return `?${params.toString()}`;
}

/**
 * Writes the filled-in query into the address bar, without reloading anything.
 *
 * Called once from `main.ts` **before the world is built**, because everything
 * that reads a world parameter reads it off `window.location` at construction
 * time (`world/wasm.ts`'s `seedFromLocation`, `session/params.ts`'s
 * `homeRequested`). `replaceState` rather than `pushState`: the session did not
 * navigate anywhere, and a back button that undid the seed would be a back
 * button that built a different world.
 *
 * Guarded rather than assumed, because this is the one call in the client that
 * touches the browser's history: a page opened from a `file://` URL or inside a
 * sandbox that refuses `replaceState` still plays — it simply plays
 * `world/wasm.ts`'s own defaults, which is the pre-Phase-4 behaviour and not a
 * crash.
 */
export function applySessionWorld(world: SessionWorld = SESSION_WORLD): void {
  const wanted = sessionWorldSearch(window.location.search, world);

  if (wanted === window.location.search) {
    return;
  }

  try {
    window.history.replaceState(null, "", `${window.location.pathname}${wanted}`);
  } catch {
    // Nothing to do and nothing worth saying on the HUD: the session is about
    // to build a world either way, and which one it built is on the readout.
  }
}

/** Whether a parameter is present and not empty. An empty value is not an answer. */
function named(params: URLSearchParams, name: string): boolean {
  const raw = params.get(name);

  return raw !== null && raw.trim() !== "";
}
